import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import LockResetRoundedIcon from '@mui/icons-material/LockResetRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import { useAuth } from '../AuthContext';
import api from '../api';
import tokens from '../theme/tokens';

const SENHA_VALIDA = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d]{8,}$/;

/**
 * Primeiro acesso de quem foi cadastrado pela diretoria: entrou com a senha temporária do e-mail
 * e precisa criar a própria senha antes de usar o app (o pop-up não fecha sem isso).
 */
const TrocarSenhaObrigatoria = () => {
  const { isAuthenticated, userLogado, token, updateUser, logout } = useAuth();
  const [senha, setSenha] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [ver, setVer] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  if (!isAuthenticated || !userLogado?.senhaTemporaria) return null;

  const valida = SENHA_VALIDA.test(senha);
  const confere = senha === confirmacao;

  const salvar = async () => {
    setErro('');
    setSalvando(true);
    try {
      // Token explícito: logo após o login ele ainda não foi gravado no localStorage que o `api` lê
      await api.post('user/senha', { novaSenha: senha }, { headers: { Authorization: `Bearer ${token}` } });
      updateUser({ senhaTemporaria: false });
    } catch (error) {
      setErro(error.response?.data?.error || 'Não foi possível salvar a senha.');
    }
    setSalvando(false);
  };

  const olho = (
    <InputAdornment position="end">
      <IconButton onClick={() => setVer(!ver)} aria-label={ver ? 'Esconder senha' : 'Mostrar senha'} edge="end">
        {ver ? <VisibilityOffRoundedIcon /> : <VisibilityRoundedIcon />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <Dialog open fullWidth maxWidth="xs" disableEscapeKeyDown>
      <DialogContent sx={{ pt: 4 }}>
        <Box sx={{ width: 72, height: 72, mx: 'auto', borderRadius: '50%', bgcolor: tokens.color.brandSoft, color: tokens.color.brand, display: 'grid', placeItems: 'center', mb: 2 }}>
          <LockResetRoundedIcon sx={{ fontSize: 40 }} />
        </Box>
        <Typography variant="h6" align="center">Crie a sua senha</Typography>
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1, mb: 2.5 }}>
          Você entrou com a senha temporária enviada por e-mail. Escolha uma senha sua para continuar.
        </Typography>
        <Stack spacing={2}>
          <TextField
            label="Nova senha"
            type={ver ? 'text' : 'password'}
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            autoComplete="new-password"
            fullWidth
            error={Boolean(senha) && !valida}
            helperText="Mínimo de 8 caracteres, com letra maiúscula, minúscula e número, sem caracteres especiais"
            InputProps={{ endAdornment: olho }}
          />
          <TextField
            label="Repita a nova senha"
            type={ver ? 'text' : 'password'}
            value={confirmacao}
            onChange={(e) => setConfirmacao(e.target.value)}
            autoComplete="new-password"
            fullWidth
            error={Boolean(confirmacao) && !confere}
            helperText={confirmacao && !confere ? 'As senhas não conferem' : ' '}
          />
          {erro && <Alert severity="error">{erro}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={logout} color="inherit">Sair</Button>
        <Button variant="contained" onClick={salvar} disabled={!valida || !confere || salvando}>Salvar senha</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TrocarSenhaObrigatoria;
